/**
 * Instanced meshes vullen, met kleur.
 *
 * Deze helper stond eerder drie keer in de scène gekopieerd (Vloer, Stellingen,
 * Lading), en in twee van die kopieën met dezelfde fout:
 *
 *     if (k && mesh.instanceColor) { ...setColorAt... }
 *
 * `instanceColor` is `null` tot de eerste `setColorAt` — three alloceert het
 * attribuut pas daar. De voorwaarde was dus per definitie onwaar bij de eerste
 * vulling, en omdat de vulling maar één keer per opbouw gebeurt, werden de
 * kleuren nóóit gezet. De kartonkleuren in `Lading.tsx` zijn sinds ze zijn
 * geschreven niet in beeld geweest: elke doos in de hal had de kleur uit het
 * materiaal in plaats van zijn eigen tint. Vandaar dat de "zes ladingtypen" in
 * beeld toch als één grijsbruine massa lazen.
 *
 * Hier staat de volgorde daarom om: eerst zetten, dan het attribuut markeren.
 */
import * as THREE from 'three'

export function vulInstanties(
  mesh: THREE.InstancedMesh | null,
  matrices: THREE.Matrix4[],
  kleuren?: THREE.Color[],
) {
  if (!mesh) return

  for (let i = 0; i < matrices.length; i++) mesh.setMatrixAt(i, matrices[i])
  mesh.instanceMatrix.needsUpdate = true

  if (kleuren && kleuren.length) {
    // setColorAt alloceert instanceColor bij de eerste aanroep.
    for (let i = 0; i < kleuren.length; i++) mesh.setColorAt(i, kleuren[i])
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true
  }

  mesh.computeBoundingSphere()
}

/** Stabiele PRNG, los van `indeling.prng` zodat de kleuren niet met de indeling mee verschuiven. */
function prng(seed: number): () => number {
  let s = (seed >>> 0) || 1
  return () => {
    s ^= s << 13; s >>>= 0
    s ^= s >>> 17
    s ^= s << 5; s >>>= 0
    return s / 4294967296
  }
}

/**
 * Kleurfactoren rond wit, die three met `material.color` vermenigvuldigt.
 *
 * Bewust ook een kleine kleurzweem en niet alleen helderheid: een partij
 * poedercoat wijkt in tint af, niet in lichtheid, en juist dat verschil is wat
 * het oog als "losse onderdelen" registreert. De rode component loopt iets
 * verder uit dan de blauwe omdat verweerd staal warmer wordt, niet koeler.
 *
 * `sterkte` 0 levert exact wit op — dan is het resultaat identiek aan geen
 * instantiekleur, dus de schuif kan echt tot nul terug.
 */
export function variatieKleuren(aantal: number, sterkte: number, seed: number): THREE.Color[] {
  if (aantal === 0) return []
  const rnd = prng(seed)
  const uit: THREE.Color[] = []
  for (let i = 0; i < aantal; i++) {
    const helder = 1 + (rnd() - 0.5) * sterkte * 2
    const warm = (rnd() - 0.5) * sterkte * 0.7
    uit.push(new THREE.Color(
      Math.max(0, helder + warm),
      Math.max(0, helder),
      Math.max(0, helder - warm),
    ))
  }
  return uit
}
